import React, { useEffect, useState, useCallback } from 'react';
import { staffAPI, EQUIPMENT_CATEGORIES } from '../../api/staff';
import { useAuth } from '../../context/AuthContext';
import { Plus, Edit2, X, Power } from 'lucide-react';
import { toast } from 'sonner';

function ItemModal({ item, onSave, onClose }) {
  const [form, setForm] = useState(item || { name: '', category: 'seguridad', description: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full px-3 py-2 border border-slate-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-soem-primary";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md animate-fade-in">
        <div className="flex items-center justify-between px-5 py-4 border-b bg-soem-navy">
          <h3 className="font-bold text-white">{item ? 'Editar artículo' : 'Nuevo artículo'}</h3>
          <button aria-label="Cerrar" onClick={onClose}><X size={18} className="text-slate-400 hover:text-white" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label htmlFor="f-nombre" className="soem-label block mb-1">Nombre *</label>
            <input id="f-nombre" className={inputCls} value={form.name} onChange={e => setForm({...form, name: e.target.value})} required />
          </div>
          <div>
            <label htmlFor="f-categoría" className="soem-label block mb-1">Categoría</label>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(EQUIPMENT_CATEGORIES).map(([k, v]) => (
                <button key={k} type="button" onClick={() => setForm({...form, category: k})}
                  className={`py-2 px-3 rounded-sm border text-xs font-semibold transition-all ${form.category === k ? 'bg-soem-navy text-white border-soem-navy' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                  {v.emoji} {v.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label htmlFor="f-descripción" className="soem-label block mb-1">Descripción</label>
            <input id="f-descripción" className={inputCls} value={form.description || ''} onChange={e => setForm({...form, description: e.target.value})} />
          </div>
          <div className="flex gap-3">
            <button disabled={saving} onClick={async () => {
              setSaving(true);
              try { await onSave(form); onClose(); }
              catch(err) { toast.error(err?.response?.data?.detail || 'Error'); }
              finally { setSaving(false); }
            }} className="soem-btn-primary flex-1 py-2.5 text-sm disabled:opacity-60">
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
            <button onClick={onClose} className="soem-btn-secondary flex-1 py-2.5 text-sm">Cancelar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function EquipmentCatalog() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await staffAPI.catalogList(); setItems(r.data.items || []); }
    catch { toast.error('Error al cargar catálogo'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSave = async (form) => {
    if (modal?.id) { await staffAPI.catalogUpdate(modal.id, form); toast.success('Artículo actualizado'); }
    else { await staffAPI.catalogCreate(form); toast.success('Artículo creado'); }
    load();
  };

  const toggleActive = async (item) => {
    try {
      await staffAPI.catalogUpdate(item.id, { ...item, is_active: !item.is_active });
      toast.success(`Artículo ${item.is_active ? 'desactivado' : 'activado'}`);
      load();
    } catch { toast.error('Error'); }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="equipment-catalog">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-soem-navy">Catálogo de Equipo</h1>
          <p className="text-slate-500 text-sm mt-1">{items.length} artículos en el catálogo</p>
        </div>
        <button onClick={() => setModal({})} className="soem-btn-primary px-4 py-2 text-sm flex items-center gap-2">
          <Plus size={16} /> Agregar artículo
        </button>
      </div>

      <div className="soem-card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="soem-table-header">
              <th className="px-5 py-3 text-left">Nombre</th>
              <th className="px-5 py-3 text-left">Categoría</th>
              <th className="px-5 py-3 text-left">Descripción</th>
              <th className="px-5 py-3 text-left">Status</th>
              <th className="px-5 py-3 text-left">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              [...Array(4)].map((_,i) => <tr key={i}><td colSpan={5} className="px-5 py-3"><div className="h-4 bg-slate-100 rounded animate-pulse" /></td></tr>)
            ) : items.map(item => {
              const cat = EQUIPMENT_CATEGORIES[item.category] || EQUIPMENT_CATEGORIES.otro;
              return (
                <tr key={item.id} className="soem-table-row">
                  <td className="px-5 py-3 font-medium text-soem-navy">{item.name}</td>
                  <td className="px-5 py-3 text-slate-500">{cat.emoji} {cat.label}</td>
                  <td className="px-5 py-3 text-slate-400 text-xs">{item.description || '—'}</td>
                  <td className="px-5 py-3">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${item.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                      {item.is_active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-5 py-3 flex items-center gap-2">
                    <button onClick={() => setModal(item)} className="text-slate-400 hover:text-soem-primary"><Edit2 size={14} /></button>
                    <button onClick={() => toggleActive(item)} className={`${item.is_active ? 'text-slate-400 hover:text-red-500' : 'text-slate-400 hover:text-green-600'}`} title={item.is_active ? 'Desactivar' : 'Activar'}>
                      <Power size={14} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {modal !== null && <ItemModal item={modal?.id ? modal : null} onSave={handleSave} onClose={() => setModal(null)} />}
    </div>
  );
}
